import React, { useCallback, useEffect, useRef } from 'react';
import { Box, useTheme } from '@mui/material';
import Editor, { OnMount } from '@monaco-editor/react';
import type { Monaco } from '@monaco-editor/react';
import type * as monacoEditor from 'monaco-editor';

// A read-only Monaco view of one file, with unified-diff line decorations for .patch/.diff content.
// Extracted from ManifestArtifactViewer so both the file-tree browser and the pattern review pane
// render patches identically (see .cursor/rules/no-duplicate-logic).

/** Infer a Monaco language from a filename extension. */
export function getLanguage(filename: string): string {
  const ext = filename.substring(filename.lastIndexOf('.'));
  const map: Record<string, string> = {
    '.patch': 'plaintext',
    '.diff': 'plaintext',
    '.json': 'json',
    '.jsonl': 'json',
    '.html': 'html',
    '.css': 'css',
    '.js': 'javascript',
    '.ts': 'typescript',
    '.xml': 'xml',
    '.yaml': 'yaml',
    '.yml': 'yaml',
    '.md': 'markdown',
  };
  return map[ext] || 'plaintext';
}

/** Whether a file is a diff/patch file (so we apply +/- decorations). */
export function isDiffFile(filename: string): boolean {
  const ext = filename.substring(filename.lastIndexOf('.'));
  return ext === '.patch' || ext === '.diff';
}

/** Apply diff line decorations to the editor. */
function applyDiffDecorations(
  editor: monacoEditor.editor.IStandaloneCodeEditor,
  monaco: Monaco,
  content: string,
  isDarkMode: boolean
) {
  const lines = content.split('\n');
  const decorations: monacoEditor.editor.IModelDeltaDecoration[] = [];

  const colors = isDarkMode
    ? {
        added: 'rgba(46, 160, 67, 0.3)',
        removed: 'rgba(248, 81, 73, 0.3)',
        hunk: 'rgba(56, 139, 253, 0.2)',
      }
    : {
        added: 'rgba(46, 160, 67, 0.15)',
        removed: 'rgba(248, 81, 73, 0.15)',
        hunk: 'rgba(56, 139, 253, 0.1)',
      };

  lines.forEach((line, index) => {
    const lineNumber = index + 1;
    let className: string | undefined;

    if (line.startsWith('+') && !line.startsWith('+++')) {
      className = 'diff-line-added';
    } else if (line.startsWith('-') && !line.startsWith('---')) {
      className = 'diff-line-removed';
    } else if (line.startsWith('@@')) {
      className = 'diff-line-hunk';
    }

    if (className) {
      decorations.push({
        range: new monaco.Range(lineNumber, 1, lineNumber, 1),
        options: { isWholeLine: true, className },
      });
    }
  });

  const styleId = 'diff-decoration-styles';
  let styleEl = document.getElementById(styleId);
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = `
    .diff-line-added { background-color: ${colors.added} !important; }
    .diff-line-removed { background-color: ${colors.removed} !important; }
    .diff-line-hunk { background-color: ${colors.hunk} !important; }
  `;

  editor.createDecorationsCollection(decorations);
}

interface PatchViewerProps {
  content: string;
  filename: string;
  height?: string | number;
}

export default function PatchViewer({ content, filename, height = '100%' }: PatchViewerProps) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const editorRef = useRef<monacoEditor.editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<Monaco | null>(null);

  useEffect(() => {
    if (editorRef.current && monacoRef.current && isDiffFile(filename)) {
      applyDiffDecorations(editorRef.current, monacoRef.current, content, isDarkMode);
    }
  }, [content, filename, isDarkMode]);

  const handleEditorMount: OnMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    if (isDiffFile(filename)) {
      applyDiffDecorations(editor, monaco, content, isDarkMode);
    }
  }, [content, filename, isDarkMode]);

  return (
    <Box sx={{ height, minHeight: 0 }}>
      <Editor
        height="100%"
        language={getLanguage(filename)}
        value={content}
        theme={isDarkMode ? 'vs-dark' : 'light'}
        onMount={handleEditorMount}
        options={{
          readOnly: true,
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          lineNumbers: 'on',
          renderLineHighlight: 'none',
          fontSize: 13,
        }}
      />
    </Box>
  );
}
